import { CLASSES, LEVELS, byId, levelOf } from '../data/classes.js';

/**
 * Who has passed what, and whether a class is sitting on the board right now.
 *
 * A ledger and a timetable, and nothing else. It does not know what a vacuum is
 * and it never scores anything: a minigame hands it a number and a target and
 * this decides only whether that was a pass and whether it is allowed to count
 * today. Keeping the games out of here is what lets all three of them be pure
 * functions somebody can test without a browser.
 *
 * The one rule with teeth is **one pass a class a day**. Failing costs you
 * nothing and you may sit the level again while the slot is up, but the fifth
 * level is five days away at the very best, which is the only thing stopping a
 * player from arriving at Tuesday holding every perk in the game. It also makes
 * the timetable mean something: a slot you let go by is a day you do not get
 * back, which is as close to a cost as an optional system is allowed to come.
 *
 * Nothing in here writes to standing, the card, or the journal, in either
 * direction. That is deliberate and it is the whole of "skipping costs little":
 * there is no hook to find, because there is no hook.
 */
export const SLOT = Object.freeze({
  /** The shift has not reached it yet. */
  BEFORE: 'before',
  /** Sit it now. */
  OPEN: 'open',
  /** Passed one of these today already. Come back tomorrow. */
  SAT: 'sat',
  /** The window closed with nothing sat in it. No penalty, just gone. */
  GONE: 'gone',
  /** All five passed. There is nothing left to teach you. */
  COMPLETE: 'complete',
  /** Off the clock, or a day with no shift on it. */
  CLOSED: 'closed',
});

export function createClasses({ catalogue = CLASSES } = {}) {
  /** Class id to how many levels are behind you. */
  let passed = new Map();
  /** Class id to the day number its last pass was banked on. */
  let passedOn = new Map();
  /** Class id to a list of every sitting, for the readouts and the tests. */
  let history = [];

  function levelsPassed(id) {
    return passed.get(id) ?? 0;
  }

  /** The level you would sit if you sat one now, or null if there is none. */
  function nextLevel(id) {
    const done = levelsPassed(id);
    return done >= LEVELS ? null : done + 1;
  }

  /**
   * Where a class stands, given a day and how far into its shift we are.
   *
   * `since` is minutes since the shift started and may be negative, which is a
   * player who turned up early. `onTheClock` is not the same question: the slot
   * is closed to somebody who has not punched in, because a class is a shift
   * you are being paid for and sitting one off the clock is not a thing.
   */
  function slotFor(entry, { day, since, onTheClock }) {
    if (nextLevel(entry.id) === null) return SLOT.COMPLETE;
    if (!onTheClock) return SLOT.CLOSED;
    if (since < entry.opensAfter) return SLOT.BEFORE;
    if (since >= entry.opensAfter + entry.window) return SLOT.GONE;
    if (passedOn.get(entry.id) === day) return SLOT.SAT;
    return SLOT.OPEN;
  }

  return {
    /** Every class with its state right now. What the board renders. */
    board({ day, since, onTheClock = true } = {}) {
      return catalogue.map((entry) => {
        const state = slotFor(entry, { day, since, onTheClock });
        const level = nextLevel(entry.id);
        return {
          entry,
          state,
          open: state === SLOT.OPEN,
          passed: levelsPassed(entry.id),
          level,
          row: level ? levelOf(entry.id, level) : null,
          /** Minutes until it opens, or until it closes. Whichever is next. */
          opensIn: Math.max(0, entry.opensAfter - since),
          closesIn: Math.max(0, entry.opensAfter + entry.window - since),
        };
      });
    },

    /** How many levels are behind you. The only thing a perk reads. */
    passed: levelsPassed,
    next: nextLevel,

    /** Every class at once, for the perks and the readouts. */
    get tiers() {
      return Object.fromEntries(catalogue.map((entry) => [entry.id, levelsPassed(entry.id)]));
    },

    get history() {
      return [...history];
    },

    /**
     * Files a sitting.
     *
     * The caller says what was scored and the row says what was needed, so the
     * decision is made once, here, and a minigame cannot award itself a pass.
     * A pass only banks if the day has not already had one, which is what stops
     * a player sitting five levels in one afternoon; the sitting is recorded
     * either way, because a run you did is a run you did.
     */
    record({ id, level, score, day }) {
      const row = levelOf(id, level);
      if (!row) return { ok: false, reason: 'no-such-level' };
      if (level !== nextLevel(id)) return { ok: false, reason: 'wrong-level' };
      const met = score >= row.target;
      const alreadyToday = passedOn.get(id) === day;
      const banked = met && !alreadyToday;
      if (banked) {
        passed.set(id, level);
        passedOn.set(id, day);
      }
      const sitting = { id, level, score, target: row.target, met, banked, day };
      history = [...history, sitting];
      return { ok: true, ...sitting, passed: levelsPassed(id) };
    },

    /**
     * Hands a class its levels, for a save that has one and for the tests.
     *
     * Clamped rather than trusted: a save written by an older build with six
     * levels in it must not hand out a sixth tier of anything, because every
     * perk table is exactly `LEVELS + 1` long and reading past the end of one
     * is an undefined multiplying a velocity.
     */
    set(id, level) {
      if (!byId[id]) return false;
      passed.set(id, Math.max(0, Math.min(LEVELS, Math.floor(level))));
      return true;
    },

    clear() {
      passed = new Map();
      passedOn = new Map();
      history = [];
    },

    toJSON: () => ({
      passed: Object.fromEntries(passed),
      passedOn: Object.fromEntries(passedOn),
      history: [...history],
    }),

    load(state) {
      passed = new Map();
      passedOn = new Map();
      history = [...(state?.history ?? [])];
      for (const [id, level] of Object.entries(state?.passed ?? {})) {
        if (byId[id]) passed.set(id, Math.max(0, Math.min(LEVELS, Math.floor(level))));
      }
      for (const [id, day] of Object.entries(state?.passedOn ?? {})) {
        if (byId[id]) passedOn.set(id, day);
      }
    },
  };
}
